import * as React from "react";
import clsx from "clsx";
import { BarChart3 } from "lucide-react";
import { AuthTransitionScreen } from "../../../ui/components/AuthTransitionScreen";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { TabSwitcher } from "../../../ui/components/TabSwitcher";
import * as formStyles from "../../../ui/components/Form/FormInput.style.css";
import { useAuth } from "../../lib/auth/hooks";
import { getApiErrorMessage } from "../../lib/api/api-error";
import {
  fetchNotificationReport,
  type NotificationReport,
  type ReportCadence,
  type ReportChannel,
} from "../../lib/api/notification-delivery";
import { useI18n } from "../../lib/i18n";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import * as s from "./NotificationReportPage.style.css";

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const MAX_RANGE_MS = 7 * DAY_MS;
const MAX_LOOKBACK_MS = 30 * DAY_MS;
const TIMELINE_LIMIT = 8;
const TABLE_LIMIT = 6;

type Preset = "24h" | "3d" | "7d" | "custom";
const PRESET_MS: Record<Exclude<Preset, "custom">, number> = {
  "24h": DAY_MS,
  "3d": 3 * DAY_MS,
  "7d": 7 * DAY_MS,
};

const REASON_KEYS: Record<string, string> = {
  flapping: "pro.notifications.report.reason.flapping",
  summary_hold: "pro.notifications.report.reason.summaryHold",
  below_immediate_level: "pro.notifications.report.reason.belowImmediate",
  resolved_off: "pro.notifications.report.reason.resolvedOff",
  paused: "pro.notifications.report.reason.paused",
  send_failed: "pro.notifications.report.reason.sendFailed",
  email_missing: "pro.notifications.report.reason.noEmail",
  phone_missing: "pro.notifications.report.reason.noPhone",
};

export function formatSpan(ms?: number | null) {
  if (ms === null || ms === undefined) return "—";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds} sec`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours < 24) return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours ? `${days} d ${restHours} hr` : `${days} d`;
}

function formatCadence(cadence?: ReportCadence) {
  if (!cadence) return "—";
  const min = formatSpan(cadence.minMs);
  const max = formatSpan(cadence.maxMs);
  return min === max
    ? `every ${formatSpan(cadence.avgMs)}`
    : `every ${formatSpan(cadence.avgMs)} (${min} – ${max})`;
}

const toLocalInput = (date: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};

const formatStamp = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export interface NotificationReportPageProps {
  userId: string;
  backLabel: string;
  onBack: () => void;
}

export function NotificationReportPage({ userId, backLabel, onBack }: NotificationReportPageProps) {
  const { t } = useI18n();
  const { accessToken } = useAuth();
  const token =
    accessToken ?? (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  const [preset, setPreset] = React.useState<Preset>("24h");
  const [customStart, setCustomStart] = React.useState(toLocalInput(new Date(Date.now() - DAY_MS)));
  const [customEnd, setCustomEnd] = React.useState(toLocalInput(new Date()));
  const [channel, setChannel] = React.useState<ReportChannel>("email");
  const [report, setReport] = React.useState<NotificationReport | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showAllTimeline, setShowAllTimeline] = React.useState(false);
  const [showAllTable, setShowAllTable] = React.useState(false);
  const requestRef = React.useRef(0);

  const rangeError = React.useMemo(() => {
    if (preset !== "custom") return null;
    const start = new Date(customStart);
    const end = new Date(customEnd);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return t("pro.notifications.report.rangeMissing");
    if (start >= end) return t("pro.notifications.report.rangeOrder");
    if (end.getTime() - start.getTime() > MAX_RANGE_MS) return t("pro.notifications.report.rangeTooLong");
    if (start.getTime() < Date.now() - MAX_LOOKBACK_MS) return t("pro.notifications.report.rangeTooOld");
    return null;
  }, [preset, customStart, customEnd, t]);

  const generate = React.useCallback(async () => {
    if (!token || rangeError) return;
    const now = new Date();
    const start =
      preset === "custom" ? new Date(customStart) : new Date(now.getTime() - PRESET_MS[preset]);
    const end = preset === "custom" ? new Date(customEnd) : now;
    const requestId = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchNotificationReport(token, userId, {
        start: start.toISOString(),
        end: end.toISOString(),
      });
      if (requestId !== requestRef.current) return;
      setReport(data);
      setShowAllTimeline(false);
      setShowAllTable(false);
      const hasEmail = data.totals.channels.email.considered > 0;
      const hasSms = data.totals.channels.sms.considered > 0;
      if (!hasEmail && hasSms) setChannel("sms");
      else if (hasEmail && !hasSms) setChannel("email");
    } catch (err) {
      if (requestId === requestRef.current) {
        setError(getApiErrorMessage(err, t("pro.notifications.report.loadError")));
      }
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [token, userId, preset, customStart, customEnd, rangeError, t]);

  React.useEffect(() => {
    void generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, token]);

  const plural = channel === "email" ? t("pro.notifications.report.emails") : t("pro.notifications.report.texts");
  const totals = report?.totals.channels[channel];
  const startMs = report ? new Date(report.range.start).getTime() : 0;
  const endMs = report ? new Date(report.range.end).getTime() : 0;
  const position = (iso: string) =>
    `${Math.min(Math.max(((new Date(iso).getTime() - startMs) / (endMs - startMs)) * 100, 0), 100)}%`;
  const filteredPct =
    totals && totals.considered > 0 ? Math.round((1 - totals.sent / totals.considered) * 100) : null;
  const funnelMax = report
    ? Math.max(report.totals.alertEvents, report.totals.episodes, totals?.considered ?? 0, 1)
    : 1;
  const products = report?.products ?? [];
  const timelineProducts = showAllTimeline ? products : products.slice(0, TIMELINE_LIMIT);
  const tableProducts = showAllTable ? products : products.slice(0, TABLE_LIMIT);
  const axis = report ? [0, 0.25, 0.5, 0.75, 1].map((f) => new Date(startMs + f * (endMs - startMs))) : [];
  const multiDay = endMs - startMs > 2 * DAY_MS;

  const funnel = (label: string, value: number, strong = false) => (
    <div className={s.funnelRow} key={label}>
      <span>{label}</span>
      <div className={s.barTrack}>
        <div
          className={strong ? s.bar.strong : s.bar.soft}
          style={{ width: `${value ? Math.max((value / funnelMax) * 100, 1) : 0}%` }}
        />
      </div>
      <span className={s.funnelValue}>{value.toLocaleString()}</span>
    </div>
  );

  return (
    <DetailPageLayout
      backLink={{ label: backLabel, onClick: onBack }}
      header={{
        media: <BarChart3 size={40} />,
        title: t("pro.notifications.report.title"),
        subtitle: report
          ? `${report.user.name || report.user.email} · ${t("pro.notifications.report.subscribed", {
              count: report.subscribedProducts,
            })} · ${formatStamp(report.range.start)} – ${formatStamp(report.range.end)}`
          : t("pro.notifications.report.subtitle"),
      }}
    >
      <div className={s.stack}>
        <div className={s.cardBody}>
          <div className={s.controls}>
            <label className={s.control}>
              <span className={formStyles.label}>{t("pro.notifications.report.range")}</span>
              <select
                className={formStyles.input}
                value={preset}
                onChange={(e) => setPreset(e.target.value as Preset)}
              >
                <option value="24h">{t("pro.notifications.report.last24h")}</option>
                <option value="3d">{t("pro.notifications.report.last3d")}</option>
                <option value="7d">{t("pro.notifications.report.last7d")}</option>
                <option value="custom">{t("pro.notifications.report.custom")}</option>
              </select>
            </label>
            {preset === "custom" ? (
              <>
                <label className={s.control}>
                  <span className={formStyles.label}>{t("pro.notifications.report.start")}</span>
                  <input
                    type="datetime-local"
                    className={formStyles.input}
                    value={customStart}
                    min={toLocalInput(new Date(Date.now() - MAX_LOOKBACK_MS))}
                    max={toLocalInput(new Date())}
                    onChange={(e) => setCustomStart(e.target.value)}
                  />
                </label>
                <label className={s.control}>
                  <span className={formStyles.label}>{t("pro.notifications.report.end")}</span>
                  <input
                    type="datetime-local"
                    className={formStyles.input}
                    value={customEnd}
                    min={toLocalInput(new Date(Date.now() - MAX_LOOKBACK_MS))}
                    max={toLocalInput(new Date())}
                    onChange={(e) => setCustomEnd(e.target.value)}
                  />
                </label>
              </>
            ) : null}
            <Button variant="solid" onClick={() => void generate()} disabled={loading || !!rangeError}>
              {loading ? t("pro.notifications.report.generating") : t("pro.notifications.report.generate")}
            </Button>
          </div>
          {rangeError ? <span className={s.errorText}>{rangeError}</span> : null}
          <span className={s.hint}>{t("pro.notifications.report.guardrails")}</span>
        </div>

        {error ? <Callout variant="error">{error}</Callout> : null}
        {loading && !report ? (
          <AuthTransitionScreen variant="compact" message={t("pro.notifications.report.generating")} />
        ) : null}

        {report && totals ? (
          <div className={clsx(s.stack, loading && s.dimmed)}>
            <div className={s.toolbar}>
              <TabSwitcher
                aria-label={t("pro.notifications.report.channel")}
                value={channel}
                onChange={(value) => setChannel(value as ReportChannel)}
                tabs={[
                  { value: "email", label: t("pro.notifications.report.email") },
                  { value: "sms", label: t("pro.notifications.report.text") },
                ]}
              />
            </div>

            <div className={s.tiles}>
              <div className={s.tile}>
                <span className={s.tileLabel}>{t("pro.notifications.report.alertEvents")}</span>
                <span className={s.tileValue}>{report.totals.alertEvents.toLocaleString()}</span>
                <span className={s.tileNote}>
                  {t("pro.notifications.report.episodesFlaps", {
                    episodes: report.totals.episodes,
                    flaps: report.totals.flaps,
                  })}
                </span>
              </div>
              <div className={s.tile}>
                <span className={s.tileLabel}>{t("pro.notifications.report.sent", { channel })}</span>
                <span className={s.tileValue}>{totals.sent.toLocaleString()}</span>
                <span className={filteredPct ? s.tileNoteGood : s.tileNote}>
                  {filteredPct !== null
                    ? t("pro.notifications.report.filtered", {
                        pct: filteredPct,
                        considered: totals.considered,
                      })
                    : t("pro.notifications.report.nothingToSend")}
                </span>
              </div>
              <div className={s.tile}>
                <span className={s.tileLabel}>{t("pro.notifications.report.longestWait")}</span>
                <span className={s.tileValue}>{formatSpan(report.metrics.longestWait?.ms)}</span>
                <span className={s.tileNote}>
                  {report.metrics.longestWait
                    ? `${report.metrics.longestWait.productName} · ${report.metrics.longestWait.alertName}`
                    : t("pro.notifications.report.noNotifications")}
                </span>
              </div>
              <div className={s.tile}>
                <span className={s.tileLabel}>{t("pro.notifications.report.fastestRepeat")}</span>
                <span className={s.tileValue}>{formatSpan(report.metrics.fastestRepeat?.ms)}</span>
                <span className={s.tileNote}>
                  {report.metrics.fastestRepeat
                    ? `${report.metrics.fastestRepeat.productName} · ${report.metrics.fastestRepeat.alertName}`
                    : t("pro.notifications.report.noRepeats")}
                </span>
              </div>
            </div>

            <div className={s.cardBody}>
              <h3 className={s.sectionTitle}>{t("pro.notifications.report.funnelTitle")}</h3>
              <p className={s.sectionSubtitle}>{t("pro.notifications.report.funnelSubtitle")}</p>
              {funnel(t("pro.notifications.report.alertEvents"), report.totals.alertEvents)}
              {funnel(t("pro.notifications.report.episodes"), report.totals.episodes)}
              {funnel(t("pro.notifications.report.considered", { channel }), totals.considered)}
              {funnel(t("pro.notifications.report.sent", { channel }), totals.sent, true)}
              <div className={s.chips}>
                {report.totals.flaps > 0 ? (
                  <span className={s.chip.warning}>
                    {t("pro.notifications.report.flapsMerged", { count: report.totals.flaps })}
                  </span>
                ) : null}
                {totals.batchedTogether > 0 ? (
                  <span className={s.chip.neutral}>
                    {t("pro.notifications.report.batched", { count: totals.batchedTogether })}
                  </span>
                ) : null}
                {Object.entries(totals.suppression)
                  .sort((a, b) => b[1] - a[1])
                  .map(([reason, count]) => (
                    <span key={reason} className={s.chip.neutral}>
                      {`${count.toLocaleString()} · ${REASON_KEYS[reason] ? t(REASON_KEYS[reason]) : reason}`}
                    </span>
                  ))}
              </div>
              <span className={s.hint}>
                {t("pro.notifications.report.sentBreakdown", {
                  new: totals.sentBreakdown.new,
                  batched: totals.sentBreakdown.batched,
                  summary: totals.sentBreakdown.summary,
                  resolved: totals.sentBreakdown.resolved,
                })}
              </span>
            </div>

            <div className={s.cardBody}>
              <h3 className={s.sectionTitle}>{t("pro.notifications.report.timeline")}</h3>
              <div className={s.legend}>
                <span>
                  <span className={s.legendTick} />
                  {t("pro.notifications.report.alertEvents")}
                </span>
                <span>
                  <span className={s.legendDot} />
                  {t("pro.notifications.report.received", { channel })}
                </span>
              </div>
              {products.length === 0 ? (
                <span className={s.empty}>{t("pro.notifications.report.empty")}</span>
              ) : (
                <div className={s.timeline}>
                  {timelineProducts.map((product) => {
                    const sent = product.timeline.sent.filter((x) => x.channel === channel);
                    return (
                      <React.Fragment key={product.id}>
                        <span className={s.timelineLabel} title={product.name}>
                          {product.name}
                        </span>
                        <div
                          className={s.lane}
                          role="img"
                          aria-label={`${product.name}: ${product.timeline.alerts.length} alert events, ${sent.length} ${plural}`}
                        >
                          <div className={s.laneLine} />
                          {product.timeline.alerts.map((iso, index) => (
                            <span key={`a-${index}`} className={s.tick} style={{ left: position(iso) }} />
                          ))}
                          {sent.map((x, index) => (
                            <span
                              key={`s-${index}`}
                              className={s.dot}
                              style={{ left: position(x.at) }}
                              title={`${formatStamp(x.at)} · ${x.kind}`}
                            />
                          ))}
                        </div>
                      </React.Fragment>
                    );
                  })}
                  <span className={s.axisSpacer} />
                  <div className={s.axis}>
                    {axis.map((tick) => (
                      <span key={tick.getTime()}>
                        {tick.toLocaleString(undefined, multiDay ? { month: "short", day: "numeric" } : { hour: "numeric" })}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {products.length > TIMELINE_LIMIT ? (
                <Button variant="text" size="sm" onClick={() => setShowAllTimeline((v) => !v)}>
                  {showAllTimeline
                    ? t("pro.notifications.report.showFewer")
                    : t("pro.notifications.report.showAll", { count: products.length })}
                </Button>
              ) : null}
            </div>

            <div className={s.cardBody}>
              <h3 className={s.sectionTitle}>{t("pro.notifications.report.byProduct")}</h3>
              <p className={s.sectionSubtitle}>{t("pro.notifications.report.cadenceHint", { channel })}</p>
              {products.length === 0 ? (
                <span className={s.empty}>{t("pro.notifications.report.empty")}</span>
              ) : (
                <div className={s.tableWrap}>
                  <table className={s.table}>
                    <thead>
                      <tr>
                        <th className={s.th}>{t("pro.notifications.report.alert")}</th>
                        <th className={s.thNum}>{t("pro.notifications.report.events")}</th>
                        <th className={s.thNum}>{t("pro.notifications.report.flaps")}</th>
                        <th className={s.thNum}>{plural}</th>
                        <th className={s.th}>{t("pro.notifications.report.cadence")}</th>
                        <th className={s.th}>{t("pro.notifications.report.status")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableProducts.map((product) => (
                        <React.Fragment key={product.id}>
                          <tr>
                            <td colSpan={6} className={s.productRow}>
                              {product.name}
                              <span className={s.productMeta}>
                                {`${product.notifications[channel]} ${plural}`}
                                {product.summaries[channel]
                                  ? ` · ${t("pro.notifications.report.inSummaries", {
                                      count: product.summaries[channel],
                                    })}`
                                  : ""}
                              </span>
                            </td>
                          </tr>
                          {product.alerts.map((alert) => (
                            <tr key={`${product.id}-${alert.code}`}>
                              <td className={s.td}>{alert.name}</td>
                              <td className={s.tdNum}>{alert.events}</td>
                              <td className={s.tdNum}>{alert.flaps}</td>
                              <td className={s.tdNum}>{alert.notifications[channel]}</td>
                              <td className={s.td}>{formatCadence(alert.cadence[channel])}</td>
                              <td className={s.td}>
                                <span className={s.status[alert.status]}>
                                  {t(`pro.notifications.report.status.${alert.status}`)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {products.length > TABLE_LIMIT ? (
                <Button variant="text" size="sm" onClick={() => setShowAllTable((v) => !v)}>
                  {showAllTable
                    ? t("pro.notifications.report.showFewer")
                    : t("pro.notifications.report.showAll", { count: products.length })}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </DetailPageLayout>
  );
}
